import AccountIcon from '@material-ui/icons/AccountCircle'
import BusinessIcon from '@material-ui/icons/Business'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import DashboardIcon from '@material-ui/icons/Dashboard'
import HeartIcon from '@material-ui/icons/Favorite'
import { GetStaticProps, GetStaticPaths } from 'next'
import React from 'react'

import type { TRoutesDashboard } from '../../components/Dashboard'
import Page from '@aletheia/common/components/Page'

import {
  StatisticsOverview,
  StatisticsDonationsByFund,
  StatisticsUsage,
  StatisticsPayouts,
} from '../../components/Statistics'
import {
  InstitutionalFunding,
  InstitutionalFundingData,
} from '../../components/Statistics/InstitutionalFunding'
import { PayoutChartDataPoint } from '../../components/Statistics/PayoutsByFund'
import { payoutStats } from '../../lib/grantsDB'
import { institutionalFundingStats } from '../../lib/institutionalFunding'

export const routes = (
  payoutData: PayoutChartDataPoint[],
  fundingSources: InstitutionalFundingData,
): TRoutesDashboard => [
  {
    href: '/[...path]',
    as: '/overview',
    label: 'Overview',
    icon: DashboardIcon,
    component: WithPayoutData(StatisticsOverview, payoutData),
  },
  {
    href: '/[...path]',
    as: '/payouts',
    label: 'Payouts',
    icon: HeartIcon,
    component: WithPayoutData(StatisticsPayouts, payoutData),
  },
  {
    href: '/[...path]',
    as: '/usage',
    label: 'Donors',
    icon: AccountIcon,
    component: StatisticsUsage,
  },
  {
    href: '/[...path]',
    as: '/funding-sources',
    label: 'Institutional Funding',
    icon: BusinessIcon,
    component: WithInstitutionalFundingData(
      InstitutionalFunding,
      fundingSources,
    ),
  },
  {
    href: '/[...path]',
    label: 'Donations',
    as: '/donations',
    component: StatisticsDonationsByFund,
    icon: CreditCardIcon,
  },
]

// this unfortunate higher-order component is necessary
// to pass data to the child route components
const WithPayoutData = (
  Component: React.ComponentType<{ data?: PayoutChartDataPoint[] }>,
  data?: PayoutChartDataPoint[],
): React.FC => {
  const withData = () => {
    return <Component data={data} />
  }
  return withData
}

const WithInstitutionalFundingData = (
  Component: React.ComponentType<{ allData: InstitutionalFundingData }>,
  allData: InstitutionalFundingData,
): React.FC => {
  const withData = () => {
    return <Component allData={allData} />
  }
  return withData
}

type StatsDashboardProps = {
  payoutsData: PayoutChartDataPoint[]
  fundingSources: InstitutionalFundingData
}

const StatsDashboard: React.FC<StatsDashboardProps> = () => {
  return (
    <Page title="Stats Dashboard" variant="wide">
      <div>Under Construction. Check back later!</div>
      {/* TODO:  bring Dashboard back online once inconsistencies have been rectified: https://forum.effectivealtruism.org/posts/mopsmd3JELJRyTTty/ozzie-gooen-s-shortform?commentId=9AeJEZ5GbXb4emfCS*/}
      {/* <Dashboard
        routes={routes(payoutsData, fundingSources)}
        basepath="/stats"
      /> */}
    </Page>
  )
}

export default StatsDashboard

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      { params: { path: ['overview'] } },
      { params: { path: ['usage'] } },
      { params: { path: ['donations'] } },
      { params: { path: ['payouts'] } },
      { params: { path: ['funding-sources'] } },
    ],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<{
  payoutsData: PayoutChartDataPoint[]
}> = async () => {
  const payoutsData = await payoutStats()
  const fundingSources = await institutionalFundingStats()

  return {
    props: {
      payoutsData,
      fundingSources,
    },
    revalidate: 60 * 60 * 24, // daily update
  }
}
